import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Login } from './pages/Login';
import { ForgotPassword } from './pages/ForgotPassword';
import { ResetPassword } from './pages/ResetPassword';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';

// Route-level code splitting. The auth pages (first paint, pre-login) stay
// eager; everything behind ProtectedRoute is React.lazy so Vite emits one
// chunk per page, fetched on first navigation. A failed chunk load (stale
// deploy) is caught by the ErrorBoundary, which offers a reload.
const named = <T,>(p: Promise<Record<string, T>>, key: string) =>
  p.then((m) => ({ default: m[key] as T }));

const Opportunities = lazy(() => named(import('./pages/Opportunities'), 'Opportunities'));
const OpportunityDetail = lazy(() => named(import('./pages/OpportunityDetail'), 'OpportunityDetail'));
const SavedSearches = lazy(() => named(import('./pages/SavedSearches'), 'SavedSearches'));
const AdminSources = lazy(() => named(import('./pages/AdminSources'), 'AdminSources'));
const AdminCoverage = lazy(() => named(import('./pages/AdminCoverage'), 'AdminCoverage'));
const AdminUsers = lazy(() => named(import('./pages/AdminUsers'), 'AdminUsers'));

export function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<div className="p-6 text-slate-500">Loading…</div>}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          <Route element={<ProtectedRoute />}>
            <Route element={<Layout />}>
              <Route index element={<Navigate to="/opportunities" replace />} />
              <Route path="/opportunities" element={<Opportunities />} />
              <Route path="/opportunities/:id" element={<OpportunityDetail />} />
              <Route path="/saved-searches" element={<SavedSearches />} />
            </Route>
          </Route>

          <Route element={<ProtectedRoute requireRole="ADMIN" />}>
            <Route element={<Layout />}>
              <Route path="/admin/sources" element={<AdminSources />} />
              <Route path="/admin/coverage" element={<AdminCoverage />} />
              <Route path="/admin/users" element={<AdminUsers />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/opportunities" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
